import React, { useRef, useEffect, useCallback, useMemo } from 'react';
import StarterKit from '@tiptap/starter-kit';
import Highlight from '@tiptap/extension-highlight';
import Placeholder from '@tiptap/extension-placeholder';
import {
  RichTextEditor,
  MenuControlsContainer,
  MenuDivider,
  MenuButtonBold,
  MenuButtonItalic,
  MenuButtonHighlightToggle,
  MenuButtonEditLink,
  LinkBubbleMenu,
  LinkBubbleMenuHandler,
} from 'mui-tiptap';
import { Box, FormHelperText, Typography } from '@mui/material';
import { markdownToHtml, htmlToMarkdown } from '../utils/markdownHtmlBridge';

export default function AboutMuiTiptapField({
  label = 'About',
  value,
  onChange,
  placeholder,
  helperText,
  rows = 4,
  fullWidth,
}) {
  const rteRef = useRef(null);
  const lastEmitted = useRef(value ?? '');
  const debounceRef = useRef(null);

  const extensions = useMemo(() => {
    const ex = [
      StarterKit.configure({
        link: {
          openOnClick: false,
          autolink: true,
          HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
        },
      }),
      Highlight.configure({ multicolor: false }),
      LinkBubbleMenuHandler,
    ];
    if (placeholder) {
      ex.push(Placeholder.configure({ placeholder }));
    }
    return ex;
  }, [placeholder]);

  const editorDependencies = useMemo(() => [extensions], [extensions]);

  const minHeight = rows * 24 + 24;

  const syncExternalValue = useCallback(() => {
    const ed = rteRef.current?.editor;
    if (!ed) return;
    const v = value ?? '';
    if (v === (lastEmitted.current ?? '')) return;
    ed.commands.setContent(markdownToHtml(v));
    lastEmitted.current = v;
  }, [value]);

  useEffect(() => {
    let cancelled = false;
    let frames = 0;
    const tick = () => {
      if (cancelled || frames++ > 120) return;
      if (rteRef.current?.editor) {
        syncExternalValue();
        return;
      }
      requestAnimationFrame(tick);
    };
    tick();
    return () => {
      cancelled = true;
    };
  }, [value, syncExternalValue]);

  const handleUpdate = useCallback(
    ({ editor }) => {
      const md = htmlToMarkdown(editor.getHTML());
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        lastEmitted.current = md;
        onChange(md);
      }, 300);
    },
    [onChange]
  );

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  return (
    <Box sx={{ width: fullWidth ? '100%' : 'auto' }}>
      <Typography
        variant="body2"
        component="label"
        sx={{ display: 'block', mb: 0.75, color: 'text.secondary' }}
      >
        {label}
      </Typography>
      <RichTextEditor
        ref={rteRef}
        extensions={extensions}
        content={markdownToHtml(value || '')}
        onUpdate={handleUpdate}
        shouldRerenderOnTransaction
        editorDependencies={editorDependencies}
        RichTextFieldProps={{
          RichTextContentProps: {
            sx: {
              minHeight,
              '& .ProseMirror': { minHeight: minHeight - 16 },
            },
          },
        }}
        renderControls={() => (
          <MenuControlsContainer>
            <MenuButtonBold />
            <MenuButtonItalic />
            <MenuDivider />
            <MenuButtonHighlightToggle />
            <MenuDivider />
            <MenuButtonEditLink />
          </MenuControlsContainer>
        )}
      >
        {(editor) => (editor ? <LinkBubbleMenu /> : null)}
      </RichTextEditor>
      {helperText ? <FormHelperText sx={{ mt: 1 }}>{helperText}</FormHelperText> : null}
    </Box>
  );
}
